import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

export default function ConfigurationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState([])
  const [allParts, setAllParts] = useState([])
  const [name, setName] = useState('')
  // Quantities keyed by part_id. Every available part starts at 0; parts already
  // in the configuration (edit mode) are pre-filled with their saved quantity.
  const [quantities, setQuantities] = useState({})

  useEffect(() => {
    // Fetch all parts first
    fetch('/api/parts')
      .then(response => response.json())
      .then(data => {
        setAllParts(data)

        if (isEdit) {
          // Fetch configuration and seed quantities from its existing parts
          fetch(`/api/configurations/${id}`)
            .then(response => response.json())
            .then(configData => {
              setName(configData.name || '')
              const seeded = {}
              configData.parts?.forEach(p => {
                seeded[p.part_id] = p.quantity
              })
              setQuantities(seeded)
              setLoading(false)
            })
            .catch(err => {
              console.error('Error fetching configuration:', err)
              setLoading(false)
            })
        } else {
          setLoading(false)
        }
      })
      .catch(err => {
        console.error('Error fetching parts:', err)
        setLoading(false)
      })
  }, [id, isEdit])

  const getQuantity = (partId) => quantities[partId] || 0

  const setQuantity = (partId, value) => {
    const qty = parseInt(value, 10)
    setQuantities(prev => ({
      ...prev,
      [partId]: isNaN(qty) || qty < 0 ? 0 : qty
    }))
  }

  const adjustQuantity = (partId, delta) => {
    setQuantities(prev => {
      const next = (prev[partId] || 0) + delta
      return { ...prev, [partId]: next < 0 ? 0 : next }
    })
  }

  const calculateTotal = () => {
    return allParts.reduce((total, part) => {
      return total + part.price * getQuantity(part.id)
    }, 0)
  }

  const selectedCount = () => {
    return allParts.filter(part => getQuantity(part.id) > 0).length
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])

    // Only parts with a positive quantity are part of the configuration
    const validParts = allParts
      .filter(part => getQuantity(part.id) > 0)
      .map(part => ({ part_id: part.id, quantity: getQuantity(part.id) }))

    const url = isEdit ? `/api/configurations/${id}` : '/api/configurations'
    const method = isEdit ? 'PUT' : 'POST'

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        configuration: {
          name: name,
          parts: validParts
        }
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          navigate('/configurations')
        }
      })
      .catch(err => {
        console.error('Error saving configuration:', err)
        setErrors(['An unexpected error occurred'])
      })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6">
        <Link to="/configurations" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Configurations
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Edit Configuration' : 'New Configuration'}
        </h1>

        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <ul className="list-disc list-inside text-red-700">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Basic B, Premium Package"
              className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Parts */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Parts</h2>
              <span className="text-sm text-gray-500">
                {selectedCount()} of {allParts.length} parts selected
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Set a quantity for each part you want to include. Parts left at 0 are not
              added to the configuration.
            </p>

            {allParts.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No parts available. <Link to="/parts/new" className="underline">Create a part first</Link>.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allParts.map((part) => {
                      const qty = getQuantity(part.id)
                      const included = qty > 0
                      return (
                        <tr key={part.id} className={included ? 'bg-green-50' : ''}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {part.description}
                            {part.vendor_name && (
                              <span className="block text-xs text-gray-400">{part.vendor_name}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">{formatCurrency(part.price)}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                type="button"
                                onClick={() => adjustQuantity(part.id, -1)}
                                disabled={qty === 0}
                                className={`w-7 h-7 flex items-center justify-center rounded-md border text-sm ${
                                  qty === 0
                                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                                aria-label={`Decrease quantity of ${part.description}`}
                              >
                                &minus;
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => setQuantity(part.id, e.target.value)}
                                className="w-16 border border-gray-300 rounded-md px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-gray-900"
                              />
                              <button
                                type="button"
                                onClick={() => adjustQuantity(part.id, 1)}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
                                aria-label={`Increase quantity of ${part.description}`}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900">
                            {included ? formatCurrency(part.price * qty) : <span className="text-gray-300">&mdash;</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                        Total:
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                        {formatCurrency(calculateTotal())}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <Link
              to="/configurations"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
            >
              {isEdit ? 'Update Configuration' : 'Create Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
